import { format } from 'date-fns';
import dayjs from 'dayjs';

import { Team } from '@graphql/generated';

const DEFAULT_TRIAL_DAYS = '30';

const getTeamStatus = (item: Team) => {
  if (item.subscriptionDetails?.subscriptionPlanName === 'Team') {
    if (
      item?.subscriptionDetails?.subscriptionStatus === 'incomplete' ||
      item?.subscriptionDetails?.subscriptionStatus === 'active'
    )
      return 'Team';
    else if (
      item?.subscriptionDetails?.subscriptionStatus === 'incomplete_expired' ||
      item?.subscriptionDetails?.subscriptionStatus === 'canceled'
    )
      return 'team-expired';
    else if (item?.subscriptionDetails?.subscriptionStatus === 'trialing')
      return 'team-trial';
  } else return 'basic';
};

const getUpgradeCTATitleAndDesc = (item: Team) => {
  let upgradeCTABtn: {
    title: string | null;
    titleInTeamDetail: string | null;
    description: string | null;
  } = {
    title: null,
    titleInTeamDetail: 'Upgrade to a Team Plan*',
    description:
      'This plan is for personal use only. Upgrade to unlock team features.',
  };
  let parsedDate = null;

  if (item?.subscriptionDetails?.expirationDate) {
    const dateAtPlanEnd =
      item?.subscriptionDetails?.expirationDate ?? '2025-03-12T05:30:00+05:30';
    parsedDate = format(dateAtPlanEnd, 'MMMM dd, yyyy');
  }

  if (!item?.subscriptionDetails && item?.personal) {
    // Basic plan
    return upgradeCTABtn;
  }

  if (!item?.subscriptionDetails && !item?.personal) {
    // Basic plan
    upgradeCTABtn = {
      title: 'Add Payment Details',
      titleInTeamDetail: 'Add Payment Details',
      description: 'Please add payment details to the team.',
    };
    return upgradeCTABtn;
  }

  // Trial ongoing - plans
  if (
    item?.subscriptionDetails?.isSubscriptionOnTrial &&
    item?.subscriptionDetails?.subscriptionStatus === 'trialing'
  ) {
    if (
      !item?.subscriptionDetails?.paymentCardLast4 ||
      item?.subscriptionDetails?.paymentCardLast4 === ''
    ) {
      // Trial ongoing, no payment info entered
      upgradeCTABtn = {
        title: 'Upgrade Now',
        titleInTeamDetail: 'Enter Payment Details to Keep Team Active',
        description: `You haven’t added a payment method. You will not be charged until your free trial ends on ${parsedDate}`,
      };
      return upgradeCTABtn;
    }
    if (item?.subscriptionDetails?.paymentCardLast4 !== '') {
      // Trial ongoing, payment info entered

      upgradeCTABtn = {
        title: null,
        titleInTeamDetail: null,
        description: null,
      };
      return upgradeCTABtn;
    }
  }
  // Trial ended - plans
  if (!item?.subscriptionDetails?.isSubscriptionOnTrial) {
    if (
      item?.subscriptionDetails?.paymentCardLast4 === '' &&
      !item?.subscriptionDetails?.isSubscriptionCanceled
    ) {
      // Trial ends, no payment info entered
      upgradeCTABtn = {
        title: 'Upgrade Now',
        titleInTeamDetail: 'Reactivate',
        description: `Your trial ended on ${parsedDate}. Add a payment method to regain access to team features.`,
      };
      return upgradeCTABtn;
    }

    if (
      item?.subscriptionDetails?.paymentCardLast4 !== '' &&
      !item?.subscriptionDetails?.isSubscriptionCanceled
    ) {
      // trial ends, and has payment method
      upgradeCTABtn = {
        title: null,
        titleInTeamDetail: null,
        description: null,
      };
      return upgradeCTABtn;
    }
  }

  // Active Plan
  if (item?.subscriptionDetails?.subscriptionStatus === 'active') {
    if (!item?.subscriptionDetails?.isSubscriptionCanceled) {
      // User is being billed

      upgradeCTABtn = {
        title: null,
        titleInTeamDetail: null,
        description: null,
      };
      return upgradeCTABtn;
    }

    const cancledButActiveDaysLeft =
      new Date(item?.subscriptionDetails?.expirationDate).getTime() >
      new Date().getTime();

    if (
      item?.subscriptionDetails?.isSubscriptionCanceled &&
      !cancledButActiveDaysLeft
    ) {
      // User canceled (still active)

      upgradeCTABtn = {
        title: 'Resume Plan',
        titleInTeamDetail: 'Reactivate',
        description: `Your subscription will end on ${parsedDate}. Resume now to avoid interruption.`,
      };
      return upgradeCTABtn;
    }
    // user cancels with expiration day in future
    else if (
      item?.subscriptionDetails?.isSubscriptionCanceled &&
      cancledButActiveDaysLeft
    ) {
      return null;
    }
  }

  // Plan canceled - Plan expired due to cancellation
  if (
    item?.subscriptionDetails?.subscriptionStatus === 'canceled' &&
    item?.subscriptionDetails?.isSubscriptionCanceled
  ) {
    // Plan expired due to cancellation
    upgradeCTABtn = {
      title: 'Renew Plan',
      titleInTeamDetail: 'Renew',
      description: `Your subscription ended on ${format(
        item?.subscriptionDetails?.expirationDate ??
          '2025-03-12T05:30:00+05:30',
        'MMMM dd, yyyy'
      )}. Renew now to regain access to team features.`,
    };
    return upgradeCTABtn;
  }
  return upgradeCTABtn;
};

/**
 * Days left in a trialing subscription, as a display string. Falls back to
 * the 30-day default when the status is not trialing, the expiration date
 * is missing, or the diff is invalid (dayjs yields NaN) — so the UI never
 * renders "NaN days".
 */
function getTrialingDaysLeft(
  subscriptionStatus: string | null | undefined,
  expirationDate: string | null | undefined
): string {
  if ((subscriptionStatus ?? '') !== 'trialing' || !expirationDate) {
    return DEFAULT_TRIAL_DAYS;
  }
  const daysLeft = -dayjs().diff(expirationDate, 'day');
  return Number.isFinite(daysLeft) && daysLeft >= 0
    ? daysLeft.toString()
    : DEFAULT_TRIAL_DAYS;
}

/**
 * Builds the saved-card expiration label. The date portion is omitted when
 * either expiry field is missing so it never renders "undefined/undefined".
 */
function formatCardExpiration(
  expMonth: string | null | undefined,
  expYear: string | null | undefined,
  isExpired: boolean
): string {
  const label = isExpired ? 'Expired!' : 'Expiration:';
  if (!expMonth || !expYear) {
    return label;
  }
  const paddedMonth = (expMonth.length === 1 ? '0' : '') + expMonth;
  return `${label} ${paddedMonth}/${expYear}`;
}

function isCardExpired(expiry: string | null | undefined): boolean {
  if (expiry && typeof expiry === 'string') {
    const [expMonth, expYear] = expiry.split('/').map(Number);
    const today = new Date();

    // Set to the **last day of the expiry month**, at 23:59:59
    const expiryDate = new Date(expYear, expMonth, 0); // This is correct: day 0 = last day of expiryMonth
    expiryDate.setHours(23, 59, 59, 999);

    return today > expiryDate;
  }
  return true;
}

export {
  formatCardExpiration,
  getTeamStatus,
  getTrialingDaysLeft,
  getUpgradeCTATitleAndDesc,
  isCardExpired,
};
